'use client';
import React, { createContext, useState, useEffect, useMemo } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { getCookie } from 'cookies-next';
import { PathNames, SetCookie } from '@/helpers/utils/siteUtils';
import { COOKIE_KEYS } from '../../constants/constant';

const InitialPageHistory = {} as any;
const UrlHistory = createContext(InitialPageHistory);

const UrlHistoryProvider = ({ children }: React.PropsWithChildren) => {
  if (typeof Node === 'function' && Node.prototype) {
    const originalRemoveChild = Node.prototype.removeChild;
    Node.prototype.removeChild = function <T extends Node>(child: T, ...args: any[]): T {
      if (child.parentNode !== this) {
        if (console) {
          console.error('Cannot remove a child from a different parent', child, this);
        }
        return child as T;
      }
      return originalRemoveChild.apply(this, [child, ...args] as any) as T;
    };

    const originalInsertBefore = Node.prototype.insertBefore;
    Node.prototype.insertBefore = function (newNode: Node, referenceNode: Node | null, ...args: any[]): any {
      if (referenceNode && referenceNode.parentNode !== this) {
        if (console) {
          console.error('Cannot insert before a reference node from a different parent', referenceNode, this);
        }
        return newNode;
      }
      return originalInsertBefore.apply(this, [newNode, referenceNode, ...args] as any);
    };
  }
  const [history, setHistory] = useState<any>(InitialPageHistory);
  const pathName = usePathname()?.split('/')?.[1];
  const searchParams = useSearchParams();
  const currentUrl = `/${pathName}/?${searchParams.toString()}`;

  useEffect(() => {
    const urlHistory = getCookie(COOKIE_KEYS.URL_HISTORY);
    if (pathName === PathNames.CUSTOMER_SEARCH || pathName === PathNames.ORDER_SEARCH) {
      switch (pathName) {
        case PathNames.CUSTOMER_SEARCH:
          return SetCookie(
            COOKIE_KEYS.URL_HISTORY,
            JSON.stringify({
              [pathName]: { link: currentUrl, name: '' },
              [PathNames.ORDER_SEARCH]: { link: '/order-search', name: '' },
            }),
          );
        case PathNames.ORDER_SEARCH:
          return SetCookie(
            COOKIE_KEYS.URL_HISTORY,
            JSON.stringify({
              [pathName]: { link: currentUrl, name: '' },
              [PathNames.CUSTOMER_SEARCH]: { link: '/customer-search', name: '' },
            }),
          );
        default:
          return;
      }
    }
    if (urlHistory) {
      const history = JSON.parse(urlHistory);
      const newHistory = { ...history };
      if (!newHistory[pathName]) {
        newHistory[pathName] = { link: currentUrl, name: '' };
      } else {
        newHistory[pathName].link = currentUrl;
      }
      setHistory(newHistory);
      SetCookie(COOKIE_KEYS.URL_HISTORY, JSON.stringify(newHistory));
    } else {
      const history = { [pathName]: { link: currentUrl, name: '' } };
      SetCookie(COOKIE_KEYS.URL_HISTORY, JSON.stringify(history));
      setHistory(history);
    }
  }, [currentUrl]);

  const updatePageName = (pageName: string, pathName: string) => {
    const newHistory = { ...history };
    if (newHistory[pathName]) {
      newHistory[pathName].name = pageName;
      setHistory(newHistory);
      SetCookie(COOKIE_KEYS.URL_HISTORY, JSON.stringify(newHistory));
    } else {
      newHistory[pathName] = { link: currentUrl, name: pageName };
      SetCookie(COOKIE_KEYS.URL_HISTORY, JSON.stringify(newHistory));
    }
  };

  const value = useMemo(() => ({ history, updatePageName }), [history]);

  return <UrlHistory.Provider value={value}>{children}</UrlHistory.Provider>;
};

export { UrlHistory, UrlHistoryProvider };
